import { useEffect, useMemo, useRef, useState } from 'react';

export default function ExerciseCard({ exercise, onComplete, onCancel }) {
  switch (exercise.kind) {
    case 'thought_record':
      return <ThoughtRecord prompt={exercise.prompt} onComplete={onComplete} onCancel={onCancel} />;
    case 'gratitude':
      return <Gratitude prompt={exercise.prompt} onComplete={onComplete} onCancel={onCancel} />;
    case 'reframe':
      return <Reframe prompt={exercise.prompt} onComplete={onComplete} onCancel={onCancel} />;
    case 'breathing':
      return <Breathing prompt={exercise.prompt} onComplete={onComplete} onCancel={onCancel} />;
    default:
      return <ExerciseShell title="Exercise"><p>{exercise.prompt}</p></ExerciseShell>;
  }
}

function ExerciseShell({ title, children, onCancel }) {
  return (
    <div className="exercise-card">
      <div className="exercise-card__header">
        <h3>{title}</h3>
        {onCancel && (
          <button className="link-btn" onClick={onCancel}>
            Skip
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function ThoughtRecord({ prompt, onComplete, onCancel }) {
  const steps = useMemo(
    () => [
      { key: 'situation', label: prompt || "What's the situation?" },
      { key: 'thought', label: "What's the automatic thought?" },
      { key: 'evidence', label: 'What evidence supports or contradicts it?' },
      { key: 'reframe', label: "What's a more balanced thought?" }
    ],
    [prompt]
  );
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const step = steps[stepIdx];
  const value = answers[step.key] || '';

  const update = (v) => setAnswers((a) => ({ ...a, [step.key]: v }));

  const next = () => {
    if (stepIdx === steps.length - 1) {
      const summary = `Thought record:\nSituation: ${answers.situation || ''}\nAutomatic thought: ${answers.thought || ''}\nEvidence: ${answers.evidence || ''}\nReframe: ${answers.reframe || ''}`.trim();
      onComplete(summary);
    } else {
      setStepIdx(stepIdx + 1);
    }
  };

  return (
    <ExerciseShell title="Thought record" onCancel={onCancel}>
      <div className="exercise-card__progress">
        Step {stepIdx + 1} of {steps.length}
      </div>
      <p className="exercise-card__prompt">{step.label}</p>
      <textarea
        className="exercise-card__input"
        value={value}
        onChange={(e) => update(e.target.value)}
        rows={4}
        autoFocus
        placeholder="Take your time…"
        aria-label={step.label}
      />
      <div className="exercise-card__actions">
        {stepIdx > 0 && (
          <button className="link-btn" onClick={() => setStepIdx(stepIdx - 1)}>
            Back
          </button>
        )}
        <button className="primary-btn" onClick={next} disabled={!value.trim()}>
          {stepIdx === steps.length - 1 ? 'Done' : 'Next'}
        </button>
      </div>
    </ExerciseShell>
  );
}

function Gratitude({ prompt, onComplete, onCancel }) {
  const [items, setItems] = useState([
    { thing: '', why: '' },
    { thing: '', why: '' },
    { thing: '', why: '' }
  ]);
  const update = (i, key, v) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));
  const ready = items.every((it) => it.thing.trim());
  const finish = () => {
    const summary = `Gratitude:\n${items
      .map((it, i) => `${i + 1}. ${it.thing}${it.why ? ` (${it.why})` : ''}`)
      .join('\n')}`;
    onComplete(summary);
  };

  return (
    <ExerciseShell title="Three good things" onCancel={onCancel}>
      <p className="exercise-card__prompt">{prompt || 'Three things that went well today, and why.'}</p>
      {items.map((it, i) => (
        <div key={i} className="exercise-card__row">
          <input
            value={it.thing}
            onChange={(e) => update(i, 'thing', e.target.value)}
            placeholder={`Thing ${i + 1}`}
            aria-label={`Good thing ${i + 1}`}
            className="exercise-card__input exercise-card__input--single"
          />
          <input
            value={it.why}
            onChange={(e) => update(i, 'why', e.target.value)}
            placeholder="Why (optional)"
            aria-label={`Why good thing ${i + 1} went well (optional)`}
            className="exercise-card__input exercise-card__input--single"
          />
        </div>
      ))}
      <div className="exercise-card__actions">
        <button className="primary-btn" onClick={finish} disabled={!ready}>
          Done
        </button>
      </div>
    </ExerciseShell>
  );
}

function Reframe({ prompt, onComplete, onCancel }) {
  const [text, setText] = useState('');
  return (
    <ExerciseShell title="Reframe" onCancel={onCancel}>
      <p className="exercise-card__prompt">{prompt || 'Name the worry as plainly as you can.'}</p>
      <textarea
        className="exercise-card__input"
        rows={4}
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What's the worry?"
        aria-label="Name the worry"
      />
      <div className="exercise-card__actions">
        <button
          className="primary-btn"
          onClick={() => onComplete(`Worry I want to reframe: ${text}`)}
          disabled={!text.trim()}
        >
          Send
        </button>
      </div>
    </ExerciseShell>
  );
}

const BREATH_PHASES = [
  { name: 'Inhale', dur: 4 },
  { name: 'Hold', dur: 7 },
  { name: 'Exhale', dur: 8 },
  { name: 'Rest', dur: 1 }
];
const BREATH_CYCLES = 4;

function Breathing({ prompt, onComplete, onCancel }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [running, setRunning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(BREATH_PHASES[0].dur);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  useEffect(() => {
    if (secondsLeft > 0) return;
    const nextPhase = (phaseIdx + 1) % BREATH_PHASES.length;
    if (nextPhase === 0) {
      const nextCycle = cycle + 1;
      if (nextCycle >= BREATH_CYCLES) {
        setRunning(false);
        return;
      }
      setCycle(nextCycle);
    }
    setPhaseIdx(nextPhase);
    setSecondsLeft(BREATH_PHASES[nextPhase].dur);
  }, [secondsLeft, phaseIdx, cycle]);

  const phase = BREATH_PHASES[phaseIdx];
  const done = !running && cycle >= BREATH_CYCLES - 1 && phaseIdx === BREATH_PHASES.length - 1;

  return (
    <ExerciseShell title="4-7-8 breathing" onCancel={onCancel}>
      <p className="exercise-card__prompt">{prompt || 'Four slow rounds. Follow the circle.'}</p>
      <div className={`breathing breathing--${phase.name.toLowerCase()}${running ? ' breathing--active' : ''}`}>
        <div className="breathing__circle" aria-hidden="true" />
        <div className="breathing__label">
          <strong role="status">{phase.name}</strong>
          <span aria-hidden="true">{secondsLeft}s</span>
        </div>
      </div>
      <div className="exercise-card__progress">
        Cycle {Math.min(cycle + 1, BREATH_CYCLES)} of {BREATH_CYCLES}
      </div>
      <div className="exercise-card__actions">
        {!running && !done && (
          <button className="primary-btn" onClick={() => setRunning(true)}>
            {cycle === 0 && phaseIdx === 0 ? 'Begin' : 'Resume'}
          </button>
        )}
        {running && (
          <button className="link-btn" onClick={() => setRunning(false)}>
            Pause
          </button>
        )}
        {done && (
          <button className="primary-btn" onClick={() => onComplete('Did the 4-7-8 breathing exercise.')}>
            Done
          </button>
        )}
      </div>
    </ExerciseShell>
  );
}
